<template>
    <div class="relative">
        <div
            v-if="product?.extensions?.categoryLabels?.length > 0"
            class="absolute top-20 left-0 z-20 flex flex-col gap-2 lg:top-24 lg:left-0 xl:left-36">
            <span
                v-for="label in product?.extensions?.categoryLabels"
                :key="label?.id"
                class="font-monument text-xxs lg:text-xss mb-0 w-max px-1.5 py-1 leading-[14px] font-bold tracking-[1.25px] lg:px-4 lg:py-2"
                :style="{
                    backgroundColor: label?.backgroundColor,
                    color: label?.color,
                }">
                {{ label?.name }}
            </span>
        </div>

        <div class="absolute top-2 left-5 z-10 lg:relative lg:left-0 lg:mt-4 lg:mb-12">
            <ShopwareProductBreadcrumbs />
        </div>
        <div class="absolute top-0 right-2 z-20 lg:top-2 lg:right-0">
            <button
                type="button"
                @click="addToWishlistSyncAndTriggerEvent(product.id)"
                class="m-2 rounded-full bg-white p-3 duration-300 hover:bg-white lg:bg-green-300"
                :aria-label="isProductInWishlist ? 'Remove from wishlist' : 'Add to wishlist'">
                <HeartIcon
                    class="size-5 stroke-2"
                    :class="isProductInWishlist ? 'fill-black text-black' : 'text-black'" />
            </button>
        </div>
        <div
            v-if="hasProductMedia"
            class="pdp-image-slider w-full overflow-hidden duration-300 lg:relative lg:flex lg:flex-col-reverse lg:gap-3 xl:min-h-[700px] xl:flex-row xl:items-start xl:gap-12">
            <Swiper
                v-if="product?.media?.length > 1"
                class="z-10! mx-0! !hidden h-[900px] lg:block! lg:h-auto lg:w-full xl:h-[900px] xl:w-24"
                @swiper="setThumbsSwiper"
                :observer="true"
                :observeParents="true"
                :slidesPerView="4.5"
                :space-between="10"
                :freeMode="true"
                :watchSlidesProgress="true"
                :breakpoints="{
                    1280: {
                        direction: 'vertical',
                    },
                }"
                :modules="[SwiperAutoplay, SwiperFreeMode, SwiperNavigation, SwiperThumbs]">
                <SwiperSlide
                    class="aspect-3/4 cursor-pointer"
                    v-for="(image, index) in [...product.media].sort((a, b) => a.position - b.position)"
                    :key="image.id"
                    @click="slideTo(index)">
                    <ShopwareImg
                        class="h-full w-full rounded-sm bg-white object-cover"
                        :src="image.media.url"
                        :alt="image.media.alt"
                        :width="96"
                        :height="128" />
                </SwiperSlide>
            </Swiper>

            <div class="main-slider relative min-h-96 w-full min-w-0 lg:min-h-0 xl:flex-1">
                <Swiper
                    class="relative aspect-3/4 w-full min-w-0 overflow-hidden lg:max-h-[900px]"
                    @swiper="onMainSwiper"
                    :observer="true"
                    :observeParents="true"
                    :modules="[SwiperAutoplay, SwiperFreeMode, SwiperNavigation, SwiperThumbs, SwiperPagination]"
                    :navigation="false"
                    :grabCursor="true"
                    :spacing="0"
                    :pagination="{
                        clickable: true,
                    }"
                    @slideChange="onSlideChange"
                    :breakpoints="{
                        1024: {
                            pagination: false,
                            navigation: {
                                nextEl: '.slider-next',
                                prevEl: '.slider-prev',
                            },
                        },
                    }"
                    :thumbs="{ swiper: thumbsSwiper }">
                    <SwiperSlide
                        class="relative h-full bg-white"
                        v-for="(image, index) in [...product.media].sort((a, b) => a.position - b.position)"
                        :key="image.id">
                        <ShopwareImg
                            @click="openLightbox"
                            class="block h-full w-full object-cover lg:rounded-sm"
                            :src="image.media.url"
                            :alt="image.media.alt"
                            :width="1200"
                            :height="1200"
                            sizes="(min-width: 1024px) 900px, 100vw"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                            :fetchpriority="index === 0 ? 'high' : undefined" />
                    </SwiperSlide>
                </Swiper>
                <div
                    class="hidden w-full lg:absolute lg:top-1/2 lg:z-10 lg:flex lg:-translate-y-1/2 lg:justify-center xl:top-[32.5%] xl:translate-y-0 2xl:top-1/2">
                    <div class="justify-between lg:flex lg:w-full">
                        <button
                            type="button"
                            class="slider-prev border-2 border-black bg-white p-3 duration-300 hover:bg-green-500">
                            <NuxtImg
                                class="size-5 rotate-180"
                                width="20"
                                height="20"
                                :alt="snippets['headless.pdp.previousImage']"
                                src="/icons/arrow.svg" />
                        </button>
                        <button
                            type="button"
                            class="slider-next border-2 border-black bg-white p-3 duration-300 hover:bg-green-500">
                            <NuxtImg
                                class="size-5"
                                width="20"
                                height="20"
                                :alt="snippets['headless.pdp.nextImage']"
                                src="/icons/arrow.svg" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-else
            class="pdp-image-slider animate-pulse overflow-hidden duration-300 xl:relative xl:flex xl:h-[700px] xl:flex-row-reverse">
            <div class="relative mb-5 w-full overflow-hidden xl:!absolute xl:left-0 xl:!mr-0 xl:mb-0 xl:max-h-[700px]">
                <div class="xl:!flex xl:max-h-[700px] xl:justify-end">
                    <div
                        class="h-96 w-full bg-gray-200 object-cover lg:aspect-3/4 lg:h-auto xl:mr-20 xl:!h-[900px] xl:max-h-[700px] xl:w-8/12"></div>
                </div>
            </div>
            <div class="hidden xl:absolute xl:top-1/2 xl:right-20 xl:z-10 xl:flex xl:w-8/12 xl:justify-between">
                <button class="slider-prev border-2 bg-gray-200 p-3"></button>
                <button class="slider-next border-2 bg-gray-200 p-3"></button>
            </div>
            <div class="h-max xl:!absolute xl:left-20 xl:!m-0 xl:w-24">
                <div class="hidden !h-32 cursor-pointer xl:block xl:!h-24">
                    <div class="mb-5 !h-32 w-full bg-gray-200 object-cover xl:!h-32"></div>
                    <div class="mb-5 !h-32 w-full bg-gray-200 object-cover xl:!h-32"></div>
                    <div class="!h-32 w-full bg-gray-200 object-cover xl:!h-32"></div>
                </div>
            </div>
        </div>

        <ShopwareProductDetailLightbox
            :isOpen="isOpen"
            :product="product"
            :initialSlide="currentSlideIndex"
            @isOpen="closeLightbox"
            @slideChange="onLightboxSlideChange" />
    </div>
</template>

<script lang="ts" setup>
    import { HeartIcon } from '@heroicons/vue/24/outline';
    import type { Swiper as SwiperType } from 'swiper/types';
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const wishlistSidebarStore = useWishlistSidebarStore();
    const { invokeWishlistSidebar } = wishlistSidebarStore;
    const { sidebarOpen } = storeToRefs(wishlistSidebarStore);

    const { trackAddToWishlist } = useGTMEcommerce();

    // Use unified wishlist for consistent behavior
    const { addToWishlist: addToUnifiedWishlist, removeFromWishlist, isInWishlist } = useUnifiedWishlist();

    // Check if current product is in wishlist
    const isProductInWishlist = computed(() => {
        return props.product?.id ? isInWishlist(props.product.id).value : false;
    });

    function triggerEvent() {
        if (props.product) {
            trackAddToWishlist(props.product);
        }
    }

    const props = defineProps({
        product: Object,
        layout: String,
    });

    const hasProductMedia = computed(() => (props.product?.media?.length ?? 0) > 0);

    const isOpen = ref(false);
    const thumbsSwiper = ref(null);
    const mainSwiper = ref<SwiperType | null>(null);
    const isButtonHidden = ref(false);
    const currentSlideIndex = ref(0);

    const setThumbsSwiper = (swiper: any) => {
        thumbsSwiper.value = swiper;
    };

    function onMainSwiper(swiper: SwiperType) {
        mainSwiper.value = swiper;
        nextTick(() => {
            swiper.update();
        });
    }

    // Centralized function to sync all sliders to a specific index
    function syncSlidersTo(index: number, animated = true) {
        currentSlideIndex.value = index;
        const animationSpeed = animated ? undefined : 0;

        if (mainSwiper.value) {
            mainSwiper.value.slideTo(index, animationSpeed);
        }
        if (thumbsSwiper.value) {
            thumbsSwiper.value.slideTo(index, animationSpeed);
        }
    }

    function slideTo(index?: number) {
        if (typeof index === 'number') {
            syncSlidersTo(index);
        }
    }

    function onSlideChange() {
        if (mainSwiper.value) {
            syncSlidersTo(mainSwiper.value.activeIndex, false);
        }
    }

    function closeLightbox() {
        if (mainSwiper.value) {
            currentSlideIndex.value = mainSwiper.value.activeIndex;
        }
        isOpen.value = false;
        isButtonHidden.value = false;
    }

    function openLightbox() {
        if (mainSwiper.value) {
            currentSlideIndex.value = mainSwiper.value.activeIndex;
        }
        isOpen.value = true;
        isButtonHidden.value = true;
    }

    function onLightboxSlideChange(newIndex: number) {
        syncSlidersTo(newIndex, false);
    }
    async function addToWishlistSyncAndTriggerEvent(productId: string) {
        if (isProductInWishlist.value) {
            // Remove from wishlist if already in wishlist
            await removeFromWishlist(productId);
        } else {
            // Add to wishlist if not in wishlist
            await addToUnifiedWishlist(productId);
            triggerEvent();
        }

        invokeWishlistSidebar();
    }
</script>

<style>
    .main-slider .swiper-horizontal > .swiper-pagination-bullets,
    .main-slider .swiper-pagination-bullets.swiper-pagination-horizontal {
        bottom: 20px !important;
        background: white;
        display: inline;
        right: 20px;
        left: auto;
        padding: 0 12px !important;
        width: fit-content;
        border-radius: 5px;
    }
</style>
